// Edit a user's display name, realm roles, and site scope — in-app, not "in Keycloak".
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Typography,
  OutlinedInput,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PROJECT_SCOPES, getRoleMeta } from "../data/mockUsersRoles";
import { listRoles } from "../api";

const EditUserModal = ({ open, user, onClose, onSave, saving = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [name, setName] = useState("");
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);
  // Assignable roles come from the BFF (live Keycloak ∩ DB catalog), not from
  // hard-coded mock metadata, so grants can only target roles that exist in the
  // realm (see audit H1).
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setRoles(user.roles || []);
      setProjects(user.projects || []);
    }
  }, [user]);

  // Load assignable roles whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    listRoles()
      .then((data) =>
        setRoleOptions(
          (data || [])
            .filter((r) => r.inKeycloak !== false)
            .map((r) => ({ key: r.name, label: r.displayName || r.name }))
        )
      )
      .catch(() => setRoleOptions([]));
  }, [open]);

  if (!user) return null;

  const accessChanged =
    JSON.stringify([...roles].sort()) !== JSON.stringify([...(user.roles || [])].sort()) ||
    JSON.stringify([...projects].sort()) !== JSON.stringify([...(user.projects || [])].sort());

  const handleSave = () => onSave({ ...user, name, roles, projects }, accessChanged);

  const labelSx = {
    fontSize: "0.625rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: colors.grey[500],
    mb: 0.5,
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 2, width: 480, maxWidth: "92vw" } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: 'var(--fx-text-primary)' }}>
        Edit {user.name}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Box>
          <Typography sx={labelSx}>Display name</Typography>
          <TextField fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} />
        </Box>

        <Box>
          <Typography sx={labelSx}>Realm roles</Typography>
          <Select
            multiple
            fullWidth
            size="small"
            value={roles}
            onChange={(e) => setRoles(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
            input={<OutlinedInput />}
            renderValue={(sel) =>
              sel
                .map((r) => roleOptions.find((o) => o.key === r)?.label || getRoleMeta(r).label)
                .join(", ")
            }
          >
            {/* Union of assignable roles + any role the user already holds, so
                an existing grant is never silently dropped from the list. */}
            {Array.from(
              new Map(
                [...roleOptions, ...roles.map((r) => ({ key: r, label: getRoleMeta(r).label }))]
                  .map((o) => [o.key, o])
              ).values()
            ).map(({ key, label }) => (
              <MenuItem key={key} value={key} sx={{ fontSize: "0.8125rem" }}>
                <Checkbox size="small" checked={roles.indexOf(key) > -1} />
                <ListItemText
                  primary={label}
                  secondary={key}
                  primaryTypographyProps={{ fontSize: "0.8125rem" }}
                  secondaryTypographyProps={{ fontSize: "0.625rem" }}
                />
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box>
          <Typography sx={labelSx}>Site scope</Typography>
          <Select
            multiple
            fullWidth
            size="small"
            value={projects}
            onChange={(e) => setProjects(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
            input={<OutlinedInput />}
            renderValue={(sel) => (sel.length ? sel.join(", ") : "Not set")}
          >
            {PROJECT_SCOPES.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: "0.8125rem" }}>
                <Checkbox size="small" checked={projects.indexOf(p) > -1} />
                <ListItemText primary={p} primaryTypographyProps={{ fontSize: "0.8125rem" }} />
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500], mt: 0.5 }}>
            Sets the <code>site_ids</code> JWT claim. Required for the Site Operator role.
          </Typography>
        </Box>

        {accessChanged && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              backgroundColor: "rgba(217,119,6,0.08)",
              border: "1px solid rgba(217,119,6,0.2)",
              borderRadius: 1.5,
              p: 1.25,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#d97706", mt: 0.1 }} />
            <Typography sx={{ fontSize: "0.6875rem", color: "var(--fx-warning-text)" }}>
              Saving will end this user's active sessions, they must sign in again to pick up the
              new permissions.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: colors.grey[400] }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal;
